// @flow
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { startOfDay, isBefore } from 'date-fns';
import clsx from 'clsx';

import AdvancedSelectField from '@shared/AdvancedSelectField';

import type { Option } from '@core/types';
import { InterventionType } from '@root/src/Intervention/types';

type InterventionsListProps = {
  interventions: InterventionType[],
  selectedInterventionId: number,
  onSelectIntervention: (interventionId: number) => void,
};

const InterventionsList = (props: InterventionsListProps): React$Node => {
  const { interventions, selectedInterventionId, onSelectIntervention } = props;
  const { t } = useTranslation();
  const today = startOfDay(new Date());

  const options = useMemo((): Option[] => (
    interventions.map((intervention) => ({
      value: String(intervention.id),
      label: intervention.name,
    }))
  ), [interventions]);

  const selectedOption = useMemo((): Option | null => {
    if (!options.length) return null;
    return options.find((option) => option.value === String(selectedInterventionId)) || options[0];
  }, [options, selectedInterventionId]);

  const handleChange = useCallback((newValue: Option | Option[] | null): void => {
    const option = Array.isArray(newValue) ? newValue[0] : newValue;
    const value = option?.value;
    if (!option || typeof value !== 'string') return;
    onSelectIntervention(Number(value));
  }, [onSelectIntervention]);

  return (
    <>
      <div className="interventions-period-filter__container interventions-weeks-select">
        <AdvancedSelectField
          name="interventions-intervention-filter"
          className="interventions-period-filter"
          label={ t('groupClasses.groupClass.interventions.interventionFilter.label') }
          labelProps={ { className: 'interventions-period-filter__label' } }
          options={ options }
          value={ selectedOption }
          onChange={ handleChange }
          isSearchable={ false }
          isClearable={ false }
        />
      </div>
      <div className="interventions-weeks-list">
        { interventions.map((intervention) => {
          const isActive = intervention.id === selectedInterventionId;
          const isExpired = isBefore(intervention.endDate, today);
          return (
            <button
              key={ intervention.id }
              type="button"
              className={ clsx('intervention-week', {
                'intervention-week--active': isActive,
                'intervention-week--expired': isExpired,
              }) }
              onClick={ () => onSelectIntervention(intervention.id) }
            >
              <div className="intervention-week__info">
                <p className="intervention-week__title">{ intervention.name }</p>
              </div>
              <span className="intervention-week__chevron">›</span>
            </button>
          );
        }) }
      </div>
    </>
  );
};

export default InterventionsList;
